import { useToast } from "@/components/ui/toast"
import {
  PaymentCashBeautyInput,
  PaymentCashBeautyOutput,
} from "@okeys/shared-utils"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { customAxios } from "../utils/customAxios/customAxios"
import { getToken } from "../utils/functions/getToken"

/**
 * Hook pour effectuer un paiement en espèces
 * Créé ou met à jour une facture avec le paiement cash
 */
export const usePaymentCashMutation = () => {
  const { addToast } = useToast()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      paymentData: PaymentCashBeautyInput
    ): Promise<PaymentCashBeautyOutput> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.post("/payment/cash", paymentData, {
        headers: {
          Authorization: token,
        },
      })

      return response.data as PaymentCashBeautyOutput
    },
    onSuccess: (data) => {
      // Invalider les queries liées aux factures et paiements
      queryClient.invalidateQueries({ queryKey: ["invoice"] })
      queryClient.invalidateQueries({ queryKey: ["collection-state-temp"] })
      queryClient.invalidateQueries({ queryKey: ["cash-register"] })

      // Message de succès basé sur le statut du paiement
      if (data.status === "completed") {
        addToast({
          type: "success",
          title: "Paiement confirmé",
          description: "Le paiement en espèces a été enregistré avec succès",
        })
      } else if (data.status === "pending") {
        addToast({
          type: "info",
          title: "Paiement partiel enregistré",
          description:
            data.message ||
            `Reste à payer: ${(data.remainingAmount / 100).toFixed(2)}€`,
        })
      }
    },
    onError: (error: Error) => {
      console.error("Erreur lors du paiement en espèces:", error)
      addToast({
        type: "error",
        title: "Erreur de paiement",
        description:
          error.message || "Impossible d'enregistrer le paiement en espèces",
      })
    },
  })
}
